/**
 * Shared formatting for course appointment conflicts (room / lecturer / cohort), used by every
 * surface that renders conflicts (the pre-save confirmation modal and the course page banner) so the
 * wording, badges and labels stay consistent.
 *
 * A "detail" is one conflict on one level: { level, with, cohorts? }, where `with` is the serialized
 * other side (a conflicting course appointment or an external room booking).
 */

const LEVEL_LABELS = { room: "Raum", lecturer: "Dozent*in", cohort: "Kohorte" };
const LEVEL_BADGES = {
	room: "text-bg-danger",
	lecturer: "text-bg-warning",
	// Purple (not Bootstrap's blue "primary") to match the timetable's cohort-conflict colour;
	// text-bg-purple is a custom utility added in app.scss.
	cohort: "text-bg-purple",
};

export function conflictLevelLabel(level) {
	return LEVEL_LABELS[level] || level;
}

export function conflictBadgeClass(level) {
	return LEVEL_BADGES[level] || "text-bg-secondary";
}

export function formatConflictDate(isoDate) {
	const [year, month, day] = (isoDate || "").split("-");
	return day && month && year ? `${day}.${month}.${year}` : isoDate;
}

// The conflicting party: a course (Veranstaltung) or an external room booking.
export function conflictPartnerLabel(withSide) {
	if (!withSide) return "einer anderen Buchung";
	if (withSide.kind === "booking") {
		return withSide.title ? `Raumbuchung „${withSide.title}“` : "Raumbuchung";
	}
	return (
		[withSide.courseNumber, withSide.courseName].filter(Boolean).join(" – ") ||
		"Veranstaltung"
	);
}

// What is affected on this conflict level: the room, the lecturer or the cohort(s).
export function conflictSubjectLabel(detail) {
	const withSide = detail.with || {};
	if (detail.level === "room") return withSide.room || "Raum";
	if (detail.level === "lecturer") return withSide.lecturer || "Dozent*in";
	// Each entry is already an aggregated group (e.g. "B.Mus. MuV - KMP 4. FS, PV 3. FS"); separate
	// multiple groups with "; " to keep the inner ", " unambiguous.
	if (detail.level === "cohort") return (detail.cohorts || []).join("; ");
	return "";
}

// Stable key to group identical conflicts (same level + same partner) so a recurring conflict can be
// collapsed into one row with all affected dates instead of one row per occurrence.
export function conflictDetailKey(detail) {
	const withSide = detail.with || {};
	const partnerId =
		withSide.kind === "booking" ? `b${withSide.bookingId}` : `c${withSide.courseId}`;
	return [
		detail.level,
		withSide.room || "",
		withSide.lecturer || "",
		(detail.cohorts || []).join(","),
		partnerId,
	].join("|");
}
